/**
 *@name spoiler-alert
 *@description Property listing with client-side search + pagination.
 */

import React, {
  useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Master from '../Master';
import './style.css';
import PropertyTile from './PropertyTile';
import IziToast from '../IziToast';
import InfoPanel from '../Panels/InfoPanel';
import Button from '../Buttons';
import PropertyAccess from '../../data/AccessRights';

const PAGE_SIZES = [6, 12, 24, 48];
const ALL = -1;

const Properties = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };
  const history = useHistory();

  // state
  const [allProperties, setAllProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (user.type !== 'admin') {
        const url = `${process.env.REACT_APP_API_URL}properties/byuser?user_id=${user.id}&take=1000&where={"status":"active"}&order={"createdat":"DESC"}`;
        try {
          const {
            data: { response },
          } = await axios.get(url, requestConfig);
          if (response.length) {
            loadProperties(response);
          } else {
            setAllProperties([]);
            IziToast('Seems you have no properties');
            setIsLoading(false);
          }
        } catch (error) {
          setIsLoading(false);
        }
      } else {
        loadProperties();
      }
    })();
  }, []);

  async function loadProperties(userProperty = []) {
    let url;
    if (user.type === 'admin') {
      url = `${process.env.REACT_APP_API_URL}property/occupancy?take=1000&where={"status":"active"}&order={"createdat":"DESC"}`;
    } else {
      const filtered = userProperty
        .map((item) => {
          return item.id;
        })
        .join();
      url = `${process.env.REACT_APP_API_URL}property/occupancy?take=1000&where={"id":"in::${filtered}","status":"active"}&order={"createdat":"DESC"}`;
    }
    try {
      const {
        data: { response: property },
      } = await axios.get(url, requestConfig);
      setAllProperties(Array.isArray(property) ? property : []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }

  // ── Search ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allProperties;
    return allProperties.filter((p) => {
      const name = String(p && p.name ? p.name : '').toLowerCase();
      const address = String(p && p.address ? p.address : '').toLowerCase();
      return name.includes(term) || address.includes(term);
    });
  }, [allProperties, search]);

  // Reset to first page whenever the filter or page size changes
  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const total = filtered.length;
  const effectiveSize = pageSize === ALL ? Math.max(total, 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(total / effectiveSize));
  const safePage = Math.min(page, pageCount);
  const startIndex = (safePage - 1) * effectiveSize;
  const pageItems = filtered.slice(startIndex, startIndex + effectiveSize);

  // Chunk the current page into rows of 3 for the 3-up grid
  const rows = [];
  for (let i = 0; i < pageItems.length; i += 3) {
    const r = pageItems.slice(i, i + 3);
    while (r.length < 3) r.push(undefined);
    rows.push(r);
  }

  const showingFrom = total === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + effectiveSize, total);

  const canCreate = user && PropertyAccess.includes(user.type);

  return (
    <Master
      redirectLink="property"
      title="Property Management"
      source="Property"
    >
      {/* ── Toolbar: search + show + create ─────────────────────────────── */}
      <div className="property-toolbar">
        <div className="property-search">
          <i className="fa fa-search" />
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="property-search__clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <i className="fa fa-times" />
            </button>
          )}
        </div>

        <div className="property-toolbar__right">
          <label className="property-show">
            <span>Show</span>
            <select
              className="form-control"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              <option value={ALL}>All</option>
            </select>
          </label>

          {canCreate && (
            <Button
              caption="Create New Property"
              cssClass="btn-success"
              onClick={() => {
                history.push({ pathname: '/newproperty' });
              }}
              type="button"
              isBlack={false}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <InfoPanel message="Loading..." />
      ) : allProperties.length === 0 ? (
        <InfoPanel message="Seems you have no properties" />
      ) : total === 0 ? (
        <InfoPanel message={`No properties match "${search}"`} />
      ) : (
        <>
          <div className="table-responsive" style={{ marginTop: '6px' }}>
            <table className="table" id="property">
              <tbody>
                {rows.map((row, rIdx) => {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`row-${safePage}-${rIdx}`}>
                      {row.map((chunk, cIdx) => {
                        return (
                          <td key={chunk ? chunk.id : `empty-${rIdx}-${cIdx}`}>
                            {chunk ? (
                              <PropertyTile
                                userToken={userToken}
                                property={chunk}
                              />
                            ) : (
                              <div className="col-md-12" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ────────────────────────────────────────────── */}
          <div className="property-pager">
            <span className="property-pager__count">
              Showing <strong>{showingFrom}</strong>–<strong>{showingTo}</strong>
              {' '}of <strong>{total}</strong>
            </span>
            {pageCount > 1 && (
              <div className="property-pager__controls">
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <i className="fa fa-chevron-left" /> Prev
                </button>
                <span className="property-pager__page">
                  Page {safePage} / {pageCount}
                </span>
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Next <i className="fa fa-chevron-right" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </Master>
  );
};

const mapStateToProps = (state) => {
  return state;
};

Properties.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
};

export default connect(mapStateToProps)(Properties);
